import { inject, Injectable } from '@angular/core';
import { BehaviorSubject } from 'rxjs';
import { ExploreService } from '../../API/explore.service/exploreservice';
import { ToastrService } from 'ngx-toastr';

interface CategoryData {
  artworks: any[];
  currentPage: number;
  totalPages: number;
  totalRecords: number;
  isLoading: boolean;
}

@Injectable({
  providedIn: 'root',
})
export class CategoryStateService {
  private categoriesSubject = new BehaviorSubject<{ [key: string]: CategoryData }>({});
  categories$ = this.categoriesSubject.asObservable();

  toastrService = inject(ToastrService);

  initializeCategories(categoryList: string[], apiService: ExploreService, predefinedCategories: string[] = []) {
    // Initialize the categories state
    const initialState: { [key: string]: CategoryData } = {};

    // Initialize from the categoryList API response
    categoryList.forEach((category) => {
      initialState[category] = {
        artworks: [],
        currentPage: 0,
        totalPages: 0,
        isLoading: false,
        totalRecords: 0,
      };
    });

    // Add predefined categories like 'Top Arts'
    predefinedCategories.forEach((category) => {
      initialState[category] = {
        artworks: [],
        currentPage: 0,
        totalPages: 0,
        isLoading: false,
        totalRecords: 0,
      };
    });

    // Now update the BehaviorSubject with the initial state
    this.categoriesSubject.next(initialState);

    // Load data for all categories, including predefined ones like 'Top Arts'
    [...categoryList, ...predefinedCategories].forEach((category) => {
      this.loadMoreData(category, apiService);
    });
  }

  loadMoreData(category: string, apiService: ExploreService) {
    const categories = this.categoriesSubject.value;
    const categoryData = categories[category];
    if (!categoryData) {
      console.error(`Category ${category} does not exist in the state.`);
      return;
    }

    if (categoryData.isLoading || (categoryData.totalPages && categoryData.currentPage >= categoryData.totalPages)) {
      return;
    }

    categoryData.isLoading = true;

    // If the category is 'Top Arts', fetch it from the API
    if (category === 'Top Arts') {
      apiService.getTopArtOfTheWeek().subscribe({
        next: (res: any) => {
          categoryData.artworks = res.data.content;
          categoryData.totalPages = res.data.totalPages;
          categoryData.totalRecords = res.data.totalRecords;
          categoryData.currentPage += 1;
          categoryData.isLoading = false;
          this.categoriesSubject.next(categories); // Trigger update after data is loaded
        },
        error: () => {
          categoryData.isLoading = false;
        },
      });
    } else {
      // Call the API for other categories
      apiService.getArtByCategory(category, categoryData.currentPage, 15).subscribe({
        next: (res: any) => {
          if(res.data && res.data.content){
            categoryData.artworks.push(...res.data.content);
            categoryData.totalPages = res.data.totalPages;
            categoryData.totalRecords = res.data.totalRecords;
            categoryData.currentPage += 1;
            categoryData.isLoading = false;
            this.categoriesSubject.next(categories); // Trigger update after data is loaded
          }
        },
        error: (err) => {
          categoryData.isLoading = false;
          this.toastrService.error(err.message)
        },
      });
    }
  }

    // Add this method to trigger category update
    triggerCategoryUpdate(category: string) {
      const categories = { ...this.categoriesSubject.value };
      if (categories[category]) {
        this.categoriesSubject.next({ ...categories });
      }
    }
  updateArtworkLikeState(artworkId: any, liked: boolean) {
    const updatedCategories = { ...this.categoriesSubject.value }; // Clone the categories object
  
    Object.keys(updatedCategories).forEach((categoryKey) => {
      const category = updatedCategories[categoryKey];
      const updatedArtworks = category.artworks.map((artwork) => {
        if (artwork.id === artworkId) {
          return { ...artwork, likeUnlikeStatus: liked }; // Return a new object for the updated artwork
        }
        return artwork;
      });
  
      // Update the category with a new reference
      updatedCategories[categoryKey] = {
        ...category,
        artworks: updatedArtworks,
      };
    });
  
    // Emit the updated state
    this.categoriesSubject.next(updatedCategories);
  }
  
}
